// src/components/CustomerFlow/Steps/ConfirmationStep.js

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCreditCard, faTruck, faFilePdf, faEye } from '@fortawesome/free-solid-svg-icons';
import api from '../../../services/api';
import { formatCurrency, formatDate, formatPercentage } from '../../../utils/numberFormatter';
import { PdfViewerModal } from '../../Common';
import { TermsModal, PromissoryModal, PrivacyModal } from '../../_wayni/Modals';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { trackMobileRequestConfirmed } from '../../../analytics/events';

const ConfirmationStep = ({ 
  flowData, 
  stepStatuses,
  onNext
}) => {
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [hasAutoActivated, setHasAutoActivated] = useState(false);
  const [requestData, setRequestData] = useState(null);
  const [contractData, setContractData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contractLoading, setContractLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  
  // Estados para los datos de la tarjeta
  const [cardLastFour, setCardLastFour] = useState('');
  const [cardBrandName, setCardBrandName] = useState('');

  // Estados para el modal del PDF
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfTitle, setPdfTitle] = useState('');

  // Estados para los modales de términos y condiciones
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showPromissoryModal, setShowPromissoryModal] = useState(false);

  // Estados para el fingerprint
  const [deviceFingerprint, setDeviceFingerprint] = useState(null);

  // Inicializar FingerprintJS
  useEffect(() => {
    const initFingerprint = async () => {
      try {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        setDeviceFingerprint(result.visitorId);
      } catch (error) {
        console.error('Error al generar fingerprint:', error);
      }
    };

    initFingerprint();
  }, []);

  // Auto-activar el paso si está en pending (solo una vez)
  useEffect(() => {
    const currentStepStatus = stepStatuses['ConfirmationStep'];
    
    // Si el estado de ConfirmationStep es pending:
    if (currentStepStatus === 'pending' && !hasAutoActivated) {
      // Marcar como auto-activado para evitar re-ejecución
      setHasAutoActivated(true);
      
      // Llamar a la API para marcar el step como in_progress
      onNext({
        step_name: 'ConfirmationStep',
        step_status: 'in_progress',
        auto_activated: true,
        activation_timestamp: new Date().toISOString()
      }, 'in_progress');
    }
  }, [stepStatuses, onNext, hasAutoActivated]);

  // Cargar datos de la solicitud de solicitud
  useEffect(() => {
    if (flowData?.tokenCustomerId && flowData?.tokenRequestId) {
      fetchRequestData();
    }
  }, [flowData]);

  // Cargar datos de la tarjeta desde localStorage (con prefijo temp_)
  useEffect(() => {
    const lastFour = localStorage.getItem('temp_card_last_four');
    const brandName = localStorage.getItem('temp_card_brand_name');
    
    if (lastFour) setCardLastFour(lastFour);
    if (brandName) setCardBrandName(brandName);
  }, []);

  // Generar contrato cuando se cargan los datos de la solicitud
  useEffect(() => {
    if (requestData && !contractData) {
      generateContract();
    }
  }, [requestData]);

  const fetchRequestData = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/customers/${flowData.tokenCustomerId}/requests/${flowData.tokenRequestId}`
      );
      setRequestData(response.data);
    } catch (error) {
      console.error('Error fetching request data:', error);
      setError('Error al cargar los datos de la solicitud');
    } finally {
      setLoading(false);
    }
  };

  const generateContract = async () => {
    try {
      setContractLoading(true);
      const response = await api.post(
        `/customers/${flowData.tokenCustomerId}/requests/${flowData.tokenRequestId}/generate-contract`
      );
      
      setContractData(response.data.contract);
    } catch (error) {
      console.error('Error generating contract:', error);
      setError('Error al generar el contrato. Por favor, inténtalo nuevamente.');
    } finally {
      setContractLoading(false);
    }
  };

  const handleConfirmRequest = async () => {
    if (!hasAcceptedTerms || isProcessing) return;

    try {
      setIsProcessing(true);

      // Enviar el fingerprint al backend
      const response = await api.post(
        `/customers/${flowData.tokenCustomerId}/requests/${flowData.tokenRequestId}/contract-signature`,
        {
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        fingerprint: deviceFingerprint,
        termsVersion: "1.0.0",
        deviceInfo: {
          userAgent: navigator.userAgent,
          language: navigator.language,
          platform: navigator.platform,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
          colorDepth: window.screen.colorDepth,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }
      });

      if (!response.data) {
        throw new Error("Error al aceptar términos");
      }
      
      // Track mobile request confirmed
      const branchId = localStorage.getItem('temp_tokenBranchId');
      const customerDni = requestData?.customer?.dni;
      trackMobileRequestConfirmed({
        commerceId: branchId || 'mobile_flow', // Fallback
        branchId: branchId || undefined,
        customerDni: customerDni,
        flowId: flowData?.flow_id
      });
      
      // Marcar el paso como completado
      const stepData = {
        step_name: 'ConfirmationStep',
        step_status: 'completed',
        request_confirmed: true,
        terms_accepted: true,
        contract_accepted: true,
        contract_id: contractData?.id,
        confirmation_timestamp: new Date().toISOString()
      };
      
      await onNext(stepData, 'completed');
    } catch (error) {
      console.error('Error al confirmar solicitud:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Función para abrir el modal del PDF del contrato
  const handleViewContract = (url, title) => {
    setPdfUrl(url);
    setPdfTitle(title);
    setShowPdfModal(true);
  };

  // Función para formatear el número de cuotas
  const formatInstallments = () => {
    if (!requestData?.financial_plan) return 'N/A';
    const { installments, installment_amount } = requestData.financial_plan;
    return `${installments} de ${formatCurrency(installment_amount)}`;
  };

  if (loading) {
    return (
      <div className="contract-signing-step bg-white p-4 rounded-3 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="mt-3">Cargando detalles de la solicitud...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="contract-signing-step bg-white p-4 rounded-3 text-center">
        <div className="alert alert-light-danger">
          <p className="mb-0">{error}</p>
          <button 
            className="btn btn-outline-danger btn-sm mt-2"
            onClick={() => {
              setError(null);
              if (requestData) {
                generateContract();
              } else {
                fetchRequestData();
              }
            }}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (!requestData) {
    return (
      <div className="contract-signing-step bg-white p-4 rounded-3 text-center">
        <div className="alert alert-warning">
          <p className="mb-0">No se pudieron cargar los datos de la solicitud</p>
        </div>
      </div>
    );
  }

  return (
    <div className="contract-signing-step bg-white p-4 rounded-3">
      <div className="text-center mb-4">
        <h3>Revisá los detalles para finalizar tu solicitud:</h3>
      </div>

      <div className="step-body">
        {/* Resumen de la solicitud */}
        <div className="request-summary mb-4">
          <div className="row">
            <div className="col-12">
              <div className="summary-item d-flex justify-content-between align-items-center py-2 border-bottom">
                <span className="fw-medium">Producto:</span>
                <span>{requestData.product?.name || 'N/A'}</span>
              </div>
              
              <div className="summary-item d-flex justify-content-between align-items-center py-2 border-bottom">
                <span className="fw-medium">Monto de la solicitud:</span>
                <span className="fw-bold text-primary">{formatCurrency(requestData.financial_plan?.original_amount || requestData.amount)}</span>
              </div>
              
              {requestData.financial_plan?.is_downpayment_plan ? (
                <>
                  <div className="summary-item d-flex justify-content-between align-items-center py-2 border-bottom">
                    <span className="fw-medium">Anticipo:</span>
                    <span>{formatCurrency(requestData.financial_plan?.downpayment_amount)} {requestData.financial_plan?.downpayment_type === 'percentage' ? `(${requestData.financial_plan?.downpayment_value}% del capital)` : `(equivalente a ${requestData.financial_plan?.downpayment_value} cuota${requestData.financial_plan?.downpayment_value > 1 ? 's' : ''})`}</span>
                  </div>
                  
                  <div className="summary-item d-flex justify-content-between align-items-center py-2 border-bottom">
                    <span className="fw-medium">Monto a Financiar:</span>
                    <span className="fw-bold text-primary">{formatCurrency(requestData.amount)}</span>
                  </div>
                </>
              ) : null}
              
              <div className="summary-item d-flex justify-content-between align-items-center py-2 border-bottom">
                <span className="fw-medium">Cuotas:</span>
                <span>{formatInstallments()}</span>
              </div>
              
              <div className="summary-item d-flex justify-content-between align-items-center py-2 border-bottom">
                <span className="fw-medium">TNA:</span>
                <span>{formatPercentage(requestData.financial_plan?.interest_rate)}</span>
              </div>
              
              <div className="summary-item d-flex justify-content-between align-items-center py-2 border-bottom">
                <span className="fw-medium">CFT con IVA:</span>
                <span>{formatPercentage(requestData.financial_plan?.cft_con_iva)}</span>
              </div>
              
              <div className="text-muted small mt-2 mb-3">
                Tu primera cuota vencerá el {requestData.financial_plan?.display_date_formatted}.
              </div>
            </div>
          </div>
        </div>

        {/* Método de pago */}
        <div className="payment-details mb-4">
          <div className="row">
            <div className="col-12">
              <div className="summary-item d-flex justify-content-between align-items-center py-2 border-bottom">
                <span className="fw-medium">
                  <FontAwesomeIcon icon={faCreditCard} className="me-2" />
                  Método de pago:
                </span>
                <span>Débito automático</span>
              </div>
              
              <div className="summary-item d-flex justify-content-between align-items-center py-2 border-bottom">
                <span className="fw-medium">Tarjeta:</span>
                <span>
                  {cardLastFour && cardBrandName ? (
                    <span className="text-success">
                      {cardBrandName.toUpperCase()?? ''} terminada en {cardLastFour?? ''}
                    </span>
                  ) : (
                    <span className="text-warning">Sin Configurar</span>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Entrega */}
        <div className="delivery-info mb-4">
          <div className="row">
            <div className="col-12">
              <div className="summary-item d-flex justify-content-between align-items-center py-2 border-bottom">
                <span className="fw-medium">
                  <FontAwesomeIcon icon={faTruck} className="me-2" />
                  Entrega:
                </span>
                <span>En el comercio</span>
              </div>
              
              <div className="text-muted small mt-2">
                Recibirás tu solicitud en el mismo comercio luego de finalizar.
              </div>
            </div>
          </div>
        </div>

        {/* Contrato */}
        <div className="contract-section mb-4">
          <div className="row">
            <div className="col-12">
              <div className="summary-item d-flex justify-content-between align-items-center py-2 border-bottom">
                <span className="fw-medium">
                  <FontAwesomeIcon icon={faFilePdf} className="me-2" />
                  Contrato:
                </span>
                <span>
                  {contractLoading ? (
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Generando...</span>
                    </div>
                  ) : contractData ? (
                    <span className="text-success">Generado</span>
                  ) : (
                    <span className="text-warning">Pendiente</span>
                  )}
                </span>
              </div>
              
              {contractData && (
                <div className="mt-2 text-end">
                  <button 
                    className="btn btn-link btn-sm text-decoration-none"
                    onClick={() => handleViewContract(contractData.url, 'Contrato de Adhesión')}
                  >
                    <FontAwesomeIcon icon={faEye} className="me-2" />
                    Ver contrato
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Términos y condiciones */}
        <div className="terms-checkbox mb-4">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="acceptTerms"
              checked={hasAcceptedTerms}
              onChange={(e) => setHasAcceptedTerms(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="acceptTerms">
              He leído los{' '}
              <a 
                href="#" 
                className="text-primary text-decoration-none"
                onClick={(e) => {
                  e.preventDefault();
                  setShowTermsModal(true);
                }}
              >
                términos y condiciones
              </a>
              {' '}y{' '}
              <a 
                href="#" 
                className="text-primary text-decoration-none"
                onClick={(e) => {
                  e.preventDefault();
                  setShowPrivacyModal(true);
                }}
              >
                política de privacidad
              </a>
              {' '}y las {' '}
              <a 
                href="#" 
                className="text-primary text-decoration-none"
                onClick={(e) => {
                  e.preventDefault();
                  setShowPromissoryModal(true);
                }}
              >
                condiciones del pagaré
              </a>
            </label>
          </div>
        </div>

        {/* Botón de confirmación */}
        <div className="text-center">
          <button
            className="btn btn-primary btn-lg w-100"
            onClick={handleConfirmRequest}
            disabled={!hasAcceptedTerms || !contractData || contractLoading || isProcessing}
          >
            {contractLoading ? 'Generando contrato...' : 
             isProcessing ? 'Procesando...' : 'Confirmar solicitud'}
          </button>
          
          {!contractData && !contractLoading && (
            <small className="text-muted d-block mt-2">
              El contrato se generará automáticamente al cargar esta página
            </small>
          )}
        </div>
      </div>

      {/* Modal para vista previa del PDF del contrato */}
      <PdfViewerModal
        show={showPdfModal}
        onHide={() => setShowPdfModal(false)}
        url={pdfUrl}
        title={pdfTitle}
      />

      {/* Modal para términos y condiciones */}
      <TermsModal
        show={showTermsModal}
        onHide={() => setShowTermsModal(false)}
      />

      {/* Modal para condiciones del pagaré */}
      <PromissoryModal
        show={showPromissoryModal}
        onHide={() => setShowPromissoryModal(false)}
        customerData={requestData?.customer}
        financialData={{
          amount: requestData?.amount,
          interest_rate: requestData?.financial_plan?.interest_rate
        }}
      />

      {/* Modal para política de privacidad */}
      <PrivacyModal
        show={showPrivacyModal}
        onHide={() => setShowPrivacyModal(false)}
      />
    </div>
  );
};

export default ConfirmationStep;
